import { DEFAULT_PRICING_RULES, ruleValue } from '@repo/db/pricing-rules'

/**
 * The Koordynator (spec §13 · H3) — one person who holds a big order together,
 * sold as a LINE on it rather than as a flag beside it.
 *
 * Spec §13 gives two modes and three numbers:
 *
 *   Opcjonalny  (2–10 pozycji)  — the client may buy one for +12 % of the
 *                                 order, minimum 39 zł.
 *   Obowiązkowy (11+ pozycji)   — switched on automatically, and free.
 *
 * and one rule that decides what both of those percentages and thresholds are
 * measured against: "Łącznie od wszystkich pozycji od sztuki (miniatury plus
 * rolki plus filmy). Rezerwacje (studio, sprzęt, produkcja) nie liczą się do
 * progu" — and, separately, "Podstawa 12 procent: tylko od pozycji od sztuki".
 *
 * ── WHY A RESERVATION IS NOT A POSITION ──────────────────────────────────
 * The threshold is a measure of how much CO-ORDINATION an order needs, and a
 * booked studio needs none: it is one room, on one day, agreed once. Ten
 * thumbnails are ten briefs, ten performers, ten deadlines and ten chats — that
 * is what somebody has to hold together, and that is what the count counts. The
 * same reasoning governs the 12 % base: charging a coordination fee on a camera
 * hire would bill for shepherding a shelf.
 *
 * ── WHY A FILM IS ONE POSITION ───────────────────────────────────────────
 * A film is priced by the MINUTE, so its `quantity` is a length, not a count.
 * Spec §13 names films among the things that count, and one film is one thing
 * to co-ordinate however long it runs — so it contributes `pieces`, which is 1
 * for anything that does not split (see `ResolvedLine.pieces`), and never its
 * minute count. Reading `quantity` here would make a 25-minute film compulsory
 * on its own.
 *
 * Pure: numbers in, numbers out. The service resolves the basket and builds the
 * row; nothing here touches a database or a clock.
 */
import type { RoundingMode } from './constants.ts'
import { roundMinor } from './pricing-engine.ts'

/**
 * +12 % of the per-piece value (spec §13) — the default for
 * `coordinator.fee_bps`, which Cennik owns.
 */
export const COORDINATOR_FEE_BPS = ruleValue(DEFAULT_PRICING_RULES, 'coordinator.fee_bps')

/** …and never less than 39 zł, however small the order (spec §13). */
export const COORDINATOR_MIN_FEE_MINOR = ruleValue(
  DEFAULT_PRICING_RULES,
  'coordinator.min_fee_net_minor',
)

/** The two numbers {@link coordinatorFeeMinor} charges, as one argument. */
export interface CoordinatorRules {
  readonly feeBps: number
  readonly minFeeMinor: number
}

/** What applied before either became editable: 12 % with a 39 zł floor. */
export const DEFAULT_COORDINATOR_RULES: CoordinatorRules = {
  feeBps: COORDINATOR_FEE_BPS,
  minFeeMinor: COORDINATOR_MIN_FEE_MINOR,
}

/**
 * The THRESHOLDS below stay constants and are deliberately not on the panel.
 * „2 do 10 pozycji" and „11+" are a statement about how much co-ordination an
 * order needs, not about what it costs — and both are written into the terms
 * the customer agreed to.
 */

/** Below two positions there is nothing to co-ordinate; the option is not offered. */
export const COORDINATOR_OPTIONAL_FROM = 2

/** From eleven the coordinator is not a choice any more (spec §13). */
export const COORDINATOR_COMPULSORY_FROM = 11

/**
 * What the basket may do about a coordinator.
 *
 *  `UNAVAILABLE` — 0 or 1 per-piece positions. Not offered, not charged.
 *  `OPTIONAL`    — 2 to 10. The client decides; the fee applies if they say yes.
 *  `COMPULSORY`  — 11 or more. Added by the server, free, whatever the client sent.
 */
export type CoordinatorMode = 'UNAVAILABLE' | 'OPTIONAL' | 'COMPULSORY'

/**
 * One basket line, reduced to the four facts §13 asks about it.
 *
 * `isReservation` is the whole of "rezerwacje nie liczą się": a line that books
 * a calendar — a room, a camera, a crew day — is out of both the count and the
 * base, whether or not it happens to have a price yet.
 */
export interface CoordinatorLine {
  /** Independent fulfilment tracks this line becomes. 1 for a film. */
  readonly pieces: number
  readonly isQuoteOnly: boolean
  /** The line books something (`offering.bookingMode !== 'NONE'`). */
  readonly isReservation: boolean
  /** What the line was frozen at, after its own ladder. NULL when quote-only. */
  readonly lineTotalNetMinor: number | null
}

/**
 * "Pozycja od sztuki" — a piece of work, not a booking and not an open question.
 *
 * Quote-only lines are excluded for the reason they are excluded from the
 * combining bonus: a line with no price cannot contribute to a percentage of a
 * price. They come back into the count on the day the quote is accepted and the
 * order is repriced, which is when there is really something to co-ordinate.
 */
export function countsTowardCoordinator(line: CoordinatorLine): boolean {
  return !line.isReservation && !line.isQuoteOnly
}

/** Spec §13's threshold count: per-piece positions, reservations excluded. */
export function coordinatorPieces(lines: readonly CoordinatorLine[]): number {
  return lines.reduce(
    (acc, line) => acc + (countsTowardCoordinator(line) ? Math.max(0, Math.trunc(line.pieces)) : 0),
    0,
  )
}

/**
 * Spec §13's 12 % base: what the per-piece half of the basket actually costs.
 *
 * It used to take the basket's MULTI_SERVICE rung as a second argument and fold
 * it in, so the fee was 12 % of what the client was actually charged rather
 * than of the list price. There is no rung any more — automatic discounting is
 * gone and a code is applied to the ORDER TOTAL, after this — so the sum of the
 * lines is already what the client pays for the work.
 */
export function coordinatorBaseMinor(lines: readonly CoordinatorLine[]): number {
  return lines.reduce(
    (acc, line) =>
      acc + (countsTowardCoordinator(line) ? Math.max(0, line.lineTotalNetMinor ?? 0) : 0),
    0,
  )
}

/** Which of spec §13's two modes this basket is in — or neither. */
export function coordinatorMode(pieces: number): CoordinatorMode {
  if (pieces >= COORDINATOR_COMPULSORY_FROM) return 'COMPULSORY'
  if (pieces >= COORDINATOR_OPTIONAL_FROM) return 'OPTIONAL'
  return 'UNAVAILABLE'
}

/**
 * What the coordinator costs on this basket.
 *
 * Zero in both of the modes that are not a purchase: `COMPULSORY` because spec
 * §13 says "bezpłatnie", `UNAVAILABLE` because there is nothing to sell. The
 * floor is applied AFTER the percentage and never instead of it, so a small
 * order pays 39 zł and a large one pays the 12 %.
 */
export function coordinatorFeeMinor(
  baseNetMinor: number,
  mode: CoordinatorMode,
  rounding: RoundingMode,
  rules: CoordinatorRules = DEFAULT_COORDINATOR_RULES,
): number {
  if (mode !== 'OPTIONAL') return 0
  const percent = roundMinor((Math.max(0, baseNetMinor) * rules.feeBps) / 10_000, rounding)
  return Math.max(percent, rules.minFeeMinor)
}

/**
 * Does this basket get a coordinator line at all?
 *
 * `wanted` is the client's tick, and it is only ever consulted in `OPTIONAL`:
 * from eleven positions the answer is yes whatever arrived on the wire, and
 * below two it is no. A tick sent for a basket that cannot have one is ignored
 * rather than refused — the checkbox is not shown at that size, so refusing
 * would fail a checkout over a field the customer never saw.
 */
export function wantsCoordinator(mode: CoordinatorMode, wanted: boolean): boolean {
  if (mode === 'COMPULSORY') return true
  return mode === 'OPTIONAL' && wanted
}

/** The line's label, frozen onto the order like every other snapshot. */
export const COORDINATOR_LABEL = 'Koordynator zamówienia'

/** What the panel and the confirmation print under it. */
export const COORDINATOR_UNIT_LABEL = 'zamówienie'
